import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { Option } from '@rivethealth/data/options';
import { MessageID } from '@rivethealth/data/translation';
import { isEqual, range } from 'lodash';
import moment from 'moment';
import { ObservableInput } from 'observable-input';
import { combineLatest, merge, Observable, Subject } from 'rxjs';
import { filter, map, scan, startWith, switchMap, tap } from 'rxjs/operators';
import { AnalyticsEventOutput } from '../analytics/common-analytics.service';
import { TranslatePipe } from '../translation/translate.pipe';
import { TranslationService } from '../translation/translation.service';

type DateParts = {
  date: number | null;
  month: number;
  year: number;
};

type ChangeDate =
  | {
      type: 'year';
      value: string;
    }
  | {
      type: 'month';
      value: string;
    }
  | {
      type: 'date';
      value: number;
    }
  | {
      type: 'today';
    }
  | {
      type: 'forward';
    }
  | {
      type: 'back';
    };

/**
 * @deprecated use riv-date instead.
 * @see https://rivethealth.github.io/design-system/?path=/story/lib-input-date--example
 */
@Component({
  selector: 'deprecated-date-picker',
  host: { class: 'rv-rows small outer' },
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeprecatedDatePickerComponent implements OnDestroy {
  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly translationService: TranslationService,
  ) {}
  ngOnDestroy(): void {
    this.translatePipe.ngOnDestroy();
  }
  private translatePipe = new TranslatePipe(this.cdr, this.translationService);

  monthMessageIds: MessageID[] = [
    'app.january',
    'app.february',
    'app.march',
    'app.april',
    'app.may',
    'app.june',
    'app.july',
    'app.august',
    'app.september',
    'app.october',
    'app.november',
    'app.december',
  ];

  get monthOptions(): Option[] {
    return this.monthMessageIds.map((month, index) => ({
      id: index.toString(),
      title: this.translatePipe.transform(month),
      subtitle: '',
    }));
  }

  yearOptions: Option[] = range(1900, moment().year() + 5).map(year => ({
    id: year.toString(),
    title: year.toString(),
    subtitle: '',
  }));

  @Input() disabled: boolean;
  @Input()
  @ObservableInput()
  date!: Observable<string>;

  @Input() min: string | null = null;
  @Input() max: string | null = null;

  @Input() clearable: boolean = false;

  @Input()
  readonly analyticsEvents: {
    changeDate?: AnalyticsEventOutput<string>;
  } = {};

  parts: Observable<DateParts> = this.date.pipe(
    filter(d => !!d),
    map(d => moment(d)),
    map(d => ({
      date: d.date(),
      month: d.month(),
      year: d.year(),
    })),
    startWith({
      date: null,
      month: moment().month(),
      year: moment().year(),
    }),
  );

  change = new Subject<ChangeDate>();

  changeParts = this.parts.pipe(
    switchMap(parts =>
      this.change.pipe(
        scan((acc, value) => {
          switch (value.type) {
            case 'year': {
              const year = +value.value;
              if (year == acc.year) {
                return acc;
              }
              return {
                ...acc,
                year: year,
                date: null,
              };
            }
            case 'month': {
              const month = +value.value;
              if (month == acc.month) {
                return acc;
              }
              return {
                ...acc,
                month: month,
                date: null,
              };
            }
            case 'date':
              return {
                ...acc,
                date: value.value,
              };
            case 'today': {
              const today = moment();
              return {
                date: today.date(),
                month: today.month(),
                year: today.year(),
              };
            }
            case 'forward': {
              const newMonth = (acc.month + 1) % 12;
              return {
                month: newMonth,
                year: acc.year + (newMonth == 0 ? 1 : 0),
                date: null,
              };
            }
            case 'back': {
              const newMonth = (acc.month - 1 + 12) % 12;
              return {
                month: newMonth,
                year: acc.year + (newMonth == 11 ? -1 : 0),
                date: null,
              };
            }
          }
        }, parts),
        map(newParts =>
          // If I've been moving months and years around, my date will by null.
          // If I make my way back to the same month and year, I might as well
          // restore the original date selection.
          newParts.date == null &&
          newParts.month == parts.month &&
          newParts.year == parts.year
            ? parts
            : newParts,
        ),
      ),
    ),
  );

  selectedParts = merge(this.parts, this.changeParts);

  today = this.selectedParts.pipe(
    map(parts => {
      const today = moment();
      return {
        visible: parts.month == today.month() && parts.year == today.year(),
        date: today.date(),
      };
    }),
  );

  dateStats = this.selectedParts.pipe(
    map(parts => {
      const m = moment().year(parts.year).month(parts.month);
      return {
        currentMonthDays: m.daysInMonth(),
        prevMonthDays: m
          .clone()
          .month(m.month() - 1)
          .daysInMonth(),
        monthStartsOn: m.clone().date(1).isoWeekday(),
      };
    }),
  );

  days = combineLatest([this.dateStats, this.selectedParts]).pipe(
    map(([{ currentMonthDays, monthStartsOn }, { year, month }]) => {
      const numRows = 6;
      const values = [];
      const min = this.min && moment(this.min);
      const max = this.max && moment(this.max);
      for (let i = 0; i < numRows * 7; i++) {
        const num = i + 1 - monthStartsOn;
        const date = new Date(year, month, num);
        values.push({
          disabled:
            num < 1 ||
            num > currentMonthDays ||
            (!!min && min.isAfter(date)) ||
            (!!max && max.isBefore(date)),
          value: num,
        });
      }
      return values;
    }),
  );

  label = this.dateStats.pipe(
    map(({ prevMonthDays, currentMonthDays }) => (num: number) => {
      if (num < 1) {
        return num + prevMonthDays;
      }
      if (num > currentMonthDays) {
        return num - currentMonthDays;
      }
      return num;
    }),
  );

  @Output() clearDate = new EventEmitter();

  @Output() changeDate = combineLatest([this.changeParts, this.parts]).pipe(
    filter(
      ([parts, oldParts]) => !isEqual(parts, oldParts) && parts.date != null,
    ),
    map(([parts]) =>
      moment()
        .year(parts.year)
        .month(parts.month)
        .date(parts.date!)
        .format('YYYY-MM-DD'),
    ),
    tap(date => {
      if (this.analyticsEvents.changeDate) {
        this.analyticsEvents.changeDate(date);
      }
    }),
  );
}
